import { Grid, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Chip, Stack, SelectChangeEvent } from '@mui/material';
import { Header } from 'components/Header/Header';
import { newServer } from '../../constants/constants';
import React, { useState, useEffect } from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const DirectorySettings: React.FC = () => {
  const [isRatingEnabled, setIsRatingEnabled] = useState<boolean>(false);
  const FONT_STYLE = { fontFamily: 'Poppins, sans-serif' };
  
  interface LawyerType {
    id: number | { id: number };
    title: string;
    description: string;
    handles: string;
    example: string;
    skills?: Skill[]; // Add skills array to lawyer type
  }
  
  interface Skill {
    id: number;
    nombre: string;
    description?: string;
  }
  
  interface TypeSkillRelation {
    id_types: number;
    id_skills: number;
    type_title?: string;
    skill_name?: string;
  }
  
  interface FormDataType {
    title: string;
    description: string;
    handles: string;
    example: string;
  }
  
  const [lawyerTypes, setLawyerTypes] = useState<LawyerType[]>([]);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<number | ''>(''); // Cambiado de array a valor único
  const [typeSkillRelations, setTypeSkillRelations] = useState<TypeSkillRelation[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataType>({ 
    title: '', 
    description: '', 
    handles: '', 
    example: '' 
  });
  const [editId, setEditId] = useState<number | null>(null);
  const [skillsOpen, setSkillsOpen] = useState<boolean>(false);
  const [currentTypeId, setCurrentTypeId] = useState<number | null>(null);

  // Fetch all data when component mounts
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = (): void => {
    // Fetch directory enabled setting
    axios.get(`${newServer}/directory/enabled`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.data;
      })
      .then((data) => setIsRatingEnabled((data as { isRatingEnabled: boolean }).isRatingEnabled))
      .catch((err) => console.error('Error fetching settings:', err));
    
    // Fetch lawyer types
    axios.get(`${newServer}/lawyer-types/get-all-lawyer-types`)
      .then((response) => {
        const types = response.data as LawyerType[];
        setLawyerTypes(types);
        
        // After fetching types, get all type-skill relations
        return axios.get(`${newServer}skill/type-skills`);
      })
      .then((response) => {
        const relations = response.data as TypeSkillRelation[];
        setTypeSkillRelations(relations);
        
        // Update lawyer types with their skills
        setLawyerTypes(prevTypes => {
          return prevTypes.map(type => {
            const typeSkills = relations
              .filter(rel => rel.id_types === type.id)
              .map(rel => ({
                id: rel.id_skills,
                nombre: rel.skill_name || ''
              }));
            
            return {
              ...type,
              skills: typeSkills
            };
          });
        });
      })
      .catch((err) => console.error('Error fetching data:', err));
    
    // Fetch all available skills
    axios.get(`${newServer}skill/skills`)
      .then((response) => setAvailableSkills(response.data as Skill[]))
      .catch((err) => console.error('Error fetching skills:', err));
  };

  const handleToggle = (): void => {
    const newState = !isRatingEnabled;
    setIsRatingEnabled(newState);

    axios.put(`${newServer}/directory`, { setting_enable_rating: newState })
      .then(() => toast.success('Rating setting updated successfully'))
      .catch((error) => {
        console.error('Error updating setting:', error);
        toast.error('Failed to update rating setting');
      });
  };

  const handleOpen = (lawyer: FormDataType = { title: '', description: '', handles: '', example: '' }, id: number | null = null): void => {
    setFormData(lawyer);
    setEditId(id);
    setSelectedSkill(''); // Resetear a valor vacío
    setOpen(true);
    
    // If editing, fetch current skills for this type
    if (id) {
      const typeWithSkills = lawyerTypes.find(type => type.id === id);
      if (typeWithSkills?.skills && typeWithSkills.skills.length > 0) {
        setSelectedSkill(typeWithSkills.skills[0].id); // Solo tomar el primer skill si existe
      }
      
      // Fetch available skills for this type (if editing)
      axios.get(`${newServer}skill/available-skills`, { params: { type_id: id } })
        .then((response) => setAvailableSkills(response.data as Skill[]))
        .catch((err) => console.error('Error fetching available skills:', err));
    } else {
      // If adding new, get all skills
      axios.get(`${newServer}skill/skills`)
        .then((response) => setAvailableSkills(response.data as Skill[]))
        .catch((err) => console.error('Error fetching skills', err));
    }
  };

  const handleClose = (): void => {
    setOpen(false);
    setEditId(null);
    setFormData({ title: '', description: '', handles: '', example: '' });
    setSelectedSkill(''); // Resetear a valor vacío
    
    // Reload data
    setTimeout(() => {
      fetchData();
    }, 2000);
  };

  const handleSubmit = (): void => {
    if (editId) {
      // Update lawyer type
      axios.put(`${newServer}/lawyer-types/update-lawyer-type/${editId}`, formData)
        .then(() => {
          // After updating type, update skills relation
          // Solo enviar el skill seleccionado si existe
          const skillIds = selectedSkill !== '' ? [selectedSkill] : [];
          return axios.post(`${newServer}skill/type-skills`, {
            type_id: editId,
            skill_ids: skillIds
          });
        })
        .then(() => {
          toast.success('Lawyer type and skill updated successfully');
          handleClose();
        })
        .catch(() => toast.error('Error updating lawyer type or skill'));
    } else {
      // Create new lawyer type
      axios.post(`${newServer}/lawyer-types/create-lawyer-type`, formData)
        .then((response) => {
            const newType = response.data as LawyerType;
             
            
            // After creating type, create skills relation (solo si hay un skill seleccionado)
            if (selectedSkill !== '') {
            return axios.post(`${newServer}skill/type-skills`, {
              type_id: typeof newType.id === 'number' ? newType.id : newType.id.id,
              skill_ids: [selectedSkill]
            }).then(() => newType);
            }
            return newType;
        })
        .then((newType) => {
          toast.success('Lawyer type and skill added successfully');
          handleClose();
        })
        .catch(() => toast.error('Error adding lawyer type or skill'));
    }
  };

  const handleDelete = (id: number): void => {
    // Check if this type has relations with skills
    const hasRelations = typeSkillRelations.some(rel => rel.id_types === id);
    
    const skill_id = typeSkillRelations.find(rel => rel.id_types === id)?.id_skills;
    
    const message = hasRelations 
      ? "This lawyer type has skill relations that will also be deleted. Do you want to continue?"
      : "You won't be able to revert this!";
    
    Swal.fire({
      title: "Are you sure?",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        // If has relations, delete them first
        const deleteRelations = hasRelations 
          ? axios.delete(`${newServer}skill/type-skills/${id}/${skill_id}`)
          : Promise.resolve();
        
        deleteRelations
          .then(() => axios.delete(`${newServer}/lawyer-types/delete-lawyer-type/${id}`))
          .then(() => {
            setLawyerTypes((prev) => prev.filter((item) => item.id !== id));
            // Also remove relations
            setTypeSkillRelations((prev) => prev.filter(rel => rel.id_types !== id));
            
            Swal.fire({
              title: "Deleted!",
              text: "Lawyer type has been deleted.",
              icon: "success"
            });
          })
          .catch(() => toast.error('Error deleting lawyer type or its relations'));
      }
    });
  };

  // Open skills management dialog
  const handleManageSkills = (typeId: number): void => {
    setCurrentTypeId(typeId);
    
    // Get available skills for this type
    axios.get(`${newServer}/skill/available-skills`, { params: { type_id: typeId } })
      .then((response) => {
        setAvailableSkills(response.data as Skill[]);
        
        // Get current skills for this type
        const typeWithSkills = lawyerTypes.find(type => type.id === typeId);
        if (typeWithSkills?.skills && typeWithSkills.skills.length > 0) {
          setSelectedSkill(typeWithSkills.skills[0].id); // Solo tomar el primer skill
        } else {
          setSelectedSkill('');
        }
        
        setSkillsOpen(true);
      })
      .catch((err) => console.error('Error fetching available skills:', err));
  };

  // Handle skills changes
  const handleSkillChange = (event: SelectChangeEvent<number | string>): void => {
    setSelectedSkill(event.target.value as number | '');
  };

  // Save skills relation
  const handleSaveSkills = (): void => {
    if (currentTypeId) {
      // Solo enviar el skill seleccionado si existe
      const skillIds = selectedSkill !== '' ? [selectedSkill] : [];
      
      axios.post(`${newServer}skill/type-skills`, {
        type_id: currentTypeId,
        skill_ids: skillIds
      })
        .then(() => {
          toast.success('Skill updated successfully');
          setSkillsOpen(false);
          fetchData();
        })
        .catch(() => toast.error('Error updating skill'));
    }
  };

  // Remove a single skill from a type
  const handleRemoveSkill = (typeId: number, skillId: number): void => {
    Swal.fire({
      title: "Remove Skill",
      text: "Are you sure you want to remove this skill from the lawyer type?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${newServer}skill/type-skills/${typeId}/${skillId}`)
          .then(() => {
            toast.success('Skill removed successfully');
            
            // Update the state to reflect the change
            setTypeSkillRelations(prev => 
              prev.filter(rel => !(rel.id_types === typeId && rel.id_skills === skillId))
            );
            
            // Update lawyer types with their skills
            setLawyerTypes(prevTypes => {
              return prevTypes.map(type => {
                if (type.id === typeId && type.skills) {
                  return {
                    ...type,
                    skills: type.skills.filter(skill => skill.id !== skillId)
                  };
                }
                return type;
              });
            });
          })
          .catch(() => toast.error('Error removing skill'));
      }
    });
  };

  return (
    <Grid className='bg-background max-h-full' style={FONT_STYLE}>
      <Header />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} style={FONT_STYLE}>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <h1 style={FONT_STYLE}>Directory Home Page Settings</h1>
        </div>
      </Box>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <FormControlLabel
          control={<Switch checked={isRatingEnabled} onChange={handleToggle} color='primary' />}
          label='Enable Rating Filter'
          style={FONT_STYLE}
        />
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <Button variant='contained' color='primary' onClick={() => handleOpen()} style={FONT_STYLE}>
          Add Lawyer Type
        </Button>
        <TableContainer 
          sx={{ ':hover': { backgroundColor: 'var(--surface-2)', cursor: 'pointer' } }}  
          component={Paper} 
          className='mt-4'
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText' }}>Title</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Description</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Handles</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Example</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Skill</TableCell>
                <TableCell sx={{ color: 'primary.contrastText' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lawyerTypes.map((lawyer) => (
                <TableRow 
                  key={typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id} 
                  sx={{ ':hover': { backgroundColor: 'var(--surface-2)', cursor: 'pointer', color: 'text.primary' } }}
                >
                  <TableCell style={FONT_STYLE}>{lawyer.title}</TableCell>
                  <TableCell style={FONT_STYLE}>{lawyer.description}</TableCell>
                  <TableCell style={FONT_STYLE}>{lawyer.handles}</TableCell>
                  <TableCell style={FONT_STYLE}>{lawyer.example}</TableCell>
                  <TableCell>
                    {lawyer.skills && lawyer.skills.length > 0 ? (
                      <Chip 
                        label={lawyer.skills[0].nombre} 
                        onDelete={() => handleRemoveSkill(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id, lawyer.skills![0].id)}
                        color="primary"
                        size="small"
                        style={{ margin: '2px' }}
                      />
                    ) : (
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleManageSkills(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}
                      >
                        Add Skill
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => handleOpen(lawyer, typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}>Edit</Button>
                    <Button color='secondary' onClick={() => handleDelete(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Add/Edit Lawyer Type Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle style={FONT_STYLE}>{editId ? 'Edit Lawyer Type' : 'Add Lawyer Type'}</DialogTitle>
        <DialogContent>
          <TextField 
            label='Title' 
            fullWidth 
            margin='dense' 
            value={formData.title} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setFormData({ ...formData, title: e.target.value })
            } 
          />
          <TextField 
            multiline 
            rows={4}  
            label='Description' 
            fullWidth 
            margin='dense' 
            value={formData.description} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setFormData({ ...formData, description: e.target.value })
            } 
          />
          <TextField 
            multiline 
            rows={4} 
            label='Handles' 
            fullWidth 
            margin='dense' 
            value={formData.handles} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setFormData({ ...formData, handles: e.target.value })
            } 
          />
          <TextField 
            multiline 
            rows={4} 
            label='Example' 
            fullWidth 
            margin='dense' 
            value={formData.example} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setFormData({ ...formData, example: e.target.value })
            } 
          />
          
          <FormControl fullWidth margin='dense'>
            <InputLabel id="skills-select-label">Skill</InputLabel>
            <Select
              labelId="skills-select-label"
              value={selectedSkill}
              onChange={handleSkillChange}
              renderValue={(selected) => {
                if (selected === null) {
                  return <em>Select a skill</em>;
                }
                const skill = availableSkills.find(s => s.id === selected);
                return skill ? skill.nombre : 'Unknown';
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableSkills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} color='primary'>{editId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Skills Dialog */}
      <Dialog open={skillsOpen} onClose={() => setSkillsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={FONT_STYLE}>Manage Skill</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin='dense'>
            <InputLabel id="manage-skills-select-label">Skill</InputLabel>
            <Select
              labelId="manage-skills-select-label"
              value={selectedSkill}
              onChange={handleSkillChange}
              renderValue={(selected) => {
                if (selected === 0) {
                  return <em>Select a skill</em>;
                }
                const skill = availableSkills.find(s => s.id === selected);
                return skill ? skill.nombre : 'Unknown';
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableSkills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillsOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSkills} color='primary'>Save</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DirectorySettings;